import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Input,
  Switch,
  FormControl,
  FormLabel,
  useToast,
  Badge,
  Divider,
  Stack,
  Spinner,
  useColorModeValue,
  Tag,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  Tooltip,
  Th,
} from "@chakra-ui/react";

import * as pdfjsLib from "pdfjs-dist";
import {
  diff_match_patch,
  DIFF_EQUAL,
  DIFF_DELETE,
  DIFF_INSERT,
} from "diff-match-patch";

import { GlobalWorkerOptions } from "pdfjs-dist";
import Nav from "../components/navbar";

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs`;


// If you pinned to v4.x, keep this. If v5.x, same line works.
// (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
//   `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;

/** ---------------- Types & Utils ---------------- */
type MoneyMap = Record<string, number>;
type StatementDateInfo = { date: Date | null; raw?: string };

function normalizeSpaces(s: string): string {
  return (s || "")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

// number like 1,234.56 or 1 234,56 or (1,234) or -2,219,200
const NUM_RE = /[-+]?[\(]?\s*\d{1,3}(?:[,\s]\d{3})*(?:[.,]\d+)?\s*[\)]?|[-+]?\d+(?:[.,]\d+)?/g;

function parseMoney(s: string): number | null {
  if (!s) return null;
  let t = s.replace(/[^\d(),.\-+]/g, "").trim();
  let negative = false;
  if (t.startsWith("(") && t.endsWith(")")) { negative = true; t = t.slice(1, -1); }
  const lastComma = t.lastIndexOf(",");
  const lastDot = t.lastIndexOf(".");
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) { t = t.replace(/\./g, "").replace(",", "."); }
    else { t = t.replace(/,/g, ""); }
  } else if (lastComma > -1 && lastDot === -1) {
    t = t.replace(/,/g, "");
  }
  const n = Number(t);
  return Number.isFinite(n) ? (negative ? -n : n) : null;
}

// rightmost number per line
function lastMoneyOnLine(line: string): number | null {
  const matches = Array.from(line.matchAll(NUM_RE)).map((m) => m[0]);
  if (!matches.length) return null;
  return parseMoney(matches[matches.length - 1]);
}

/** ---------------- PDF extraction ---------------- */
async function extractPdfLinesPerPage(file: File): Promise<string[][]> {
  const ab = await file.arrayBuffer();
  const pdf = await (pdfjsLib as any).getDocument({ data: ab }).promise;
  const perPageLines: string[][] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    // bucket by Y; collect text with X for ordering
    const buckets = new Map<number, Array<{ x: number; s: string }>>();
    for (const it of content.items as any[]) {
      if (!it?.str) continue;
      const s: string = it.str;
      const t = it.transform || it.matrix || [];
      const x = typeof t[4] === "number" ? t[4] : 0;
      const y = typeof t[5] === "number" ? Math.round(t[5]) : 0;
      if (!buckets.has(y)) buckets.set(y, []);
      buckets.get(y)!.push({ x, s });
    }
    const ys = Array.from(buckets.keys()).sort((a, b) => b - a);
    const lines = ys.map((y) =>
      normalizeSpaces(buckets.get(y)!.sort((a, b) => a.x - b.x).map(p => p.s).join(" "))
    ).filter(Boolean);

    perPageLines.push(lines);
  }
  return perPageLines;
}

/** ---------------- Template & parsing ---------------- */
const REQUIRED_KEYS = [
  "Revenue",
  "Cost of Goods Sold (COGS)",
  "Gross Profit",
  "Operating Expenses",
  "Operating Income",
  "Other Income",
  "Net Profit",
  "Drawings",
  "Net Profit after Drawings",
];

// label patterns (tweak as needed)
const LABEL_DEFS: { key: string; patterns: RegExp[] }[] = [
  { key: "Revenue", patterns: [/^revenue\b/i, /\bsales\b/i, /\bturnover\b/i] },

  { key: "Cost of Goods Sold (COGS)", patterns: [/^cost of goods sold\b/i, /\bCOGS\b/i, /^cost of sales\b/i] },
  { key: "Factory Overheads", patterns: [/^factory overheads?\b/i] },
  { key: "Raw Materials", patterns: [/^raw materials?\b/i] },
  { key: "Direct Labor", patterns: [/^direct labour\b|^direct labor\b/i] },
  { key: "Carriage Inwards", patterns: [/^carriage inwards?\b/i] },

  { key: "Gross Profit", patterns: [/^gross profit\b/i] },

  { key: "Operating Expenses", patterns: [/^operating expenses?\b/i] },
  { key: "Selling & Distribution Exp.", patterns: [/^selling *(?:&|and) *distribution.*$/i] },
  { key: "Administrative Expenses", patterns: [/^administrative expenses?\b|^administrative\b|\bG&A\b/i] },
  { key: "Other Expenses", patterns: [/^other expenses?\b/i] },

  { key: "Operating Income", patterns: [/^operating (income|profit)\b|\bEBIT\b/i] },
  { key: "Other Income", patterns: [/^other income\b/i] },
  { key: "Net Profit", patterns: [/^net (profit|income|loss)\b|\bPAT\b/i] },
  { key: "Drawings", patterns: [/^drawings\b/i] },
  { key: "Net Profit after Drawings", patterns: [/^net profit after drawings\b/i] },
];

function isIncomeStatement(lines: string[]): boolean {
  const blob = lines.join(" ").toLowerCase();
  return /\bincome statement\b|\bstatement of profit\b|\bprofit and loss\b/.test(blob);
}

/** ---- Date parsing under the heading ---- */
const MONTHS: Record<string, number> = {
  jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,sept:8,oct:9,nov:10,dec:11
};

function parseDateFlexible(s: string): Date | null {
  const str = s.replace(/(\d)(st|nd|rd|th)\b/gi, "$1").trim();

  // 30 Sep 2025 / 30 September 2025
  let m = str.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (m) {
    const d = parseInt(m[1],10);
    const mon = MONTHS[m[2].toLowerCase()] ?? MONTHS[m[2].slice(0,3).toLowerCase()];
    const y = parseInt(m[3],10);
    if (Number.isFinite(d) && mon != null && Number.isFinite(y)) return new Date(y, mon, d);
  }
  // September 30, 2025
  m = str.match(/([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/);
  if (m) {
    const mon = MONTHS[m[1].toLowerCase()] ?? MONTHS[m[1].slice(0,3).toLowerCase()];
    const d = parseInt(m[2],10);
    const y = parseInt(m[3],10);
    if (mon != null && Number.isFinite(d) && Number.isFinite(y)) return new Date(y, mon, d);
  }
  // dd/mm/yyyy or dd-mm-yyyy
  m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
  if (m) {
    const d = parseInt(m[1],10), mon = parseInt(m[2],10)-1, y = parseInt(m[3].length===2?("20"+m[3]):m[3],10);
    if (Number.isFinite(d) && Number.isFinite(mon) && Number.isFinite(y)) return new Date(y, mon, d);
  }
  // yyyy-mm-dd
  m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const y = parseInt(m[1],10), mon = parseInt(m[2],10)-1, d = parseInt(m[3],10);
    if (Number.isFinite(d) && Number.isFinite(mon) && Number.isFinite(y)) return new Date(y, mon, d);
  }
  return null;
}

function parseStatementEndDate(lines: string[]): StatementDateInfo {
  const head = lines.slice(0, 40); // look near the top
  for (const line of head) {
    const l = line.toLowerCase();
    // "For the period/year/month/quarter ended <date>"
    if (/for the (year|period|month|quarter)\s+ended/i.test(l)) {
      const after = line.split(/ended/i)[1] || "";
      const d = parseDateFlexible(after);
      if (d) return { date: d, raw: after.trim() };
    }
    // "Quarter ended <date>"
    if (/quarter\s+ended/i.test(l)) {
      const after = line.split(/ended/i)[1] || "";
      const d = parseDateFlexible(after);
      if (d) return { date: d, raw: after.trim() };
    }
    // "For the period <date> to <date>" -> take the last date
    if (/for the (year|period|month|quarter).+ to /i.test(l)) {
      const parts = line.split(/to/i);
      const tail = parts[parts.length - 1] || "";
      const d = parseDateFlexible(tail);
      if (d) return { date: d, raw: tail.trim() };
    }
  }
  // fallback: any date-looking thing in top area; pick latest
  const candidates: Date[] = [];
  for (const line of head) {
    const d1 = parseDateFlexible(line);
    if (d1) candidates.push(d1);
  }
  if (candidates.length) {
    candidates.sort((a,b)=>a.getTime()-b.getTime());
    const d = candidates[candidates.length-1];
    return { date: d, raw: undefined };
  }
  return { date: null };
}

function formatDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function extractMoneyMap(lines: string[]): MoneyMap {
  const out: MoneyMap = {};
  const lower = lines.map((l) => l.toLowerCase());
  for (const def of LABEL_DEFS) {
    const idx = lower.findIndex((l) => def.patterns.some((p) => p.test(l)));
    if (idx >= 0) {
      const amt = lastMoneyOnLine(lines[idx]);
      if (amt != null) out[def.key] = amt;
    }
  }
  return out;
}

function missingRequiredKeys(map: MoneyMap): string[] {
  return REQUIRED_KEYS.filter((k) => !(k in map));
}

export function PDFCompareScreen() {
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const [leftMap, setLeftMap] = useState<MoneyMap | null>(null);   // older
  const [rightMap, setRightMap] = useState<MoneyMap | null>(null); // newer
  const [errors, setErrors] = useState<string[]>([]);
  const [dateWarnings, setDateWarnings] = useState<string[]>([]);
  const [leftMeta, setLeftMeta] = useState<{ name: string; date: Date | null } | null>(null);
  const [rightMeta, setRightMeta] = useState<{ name: string; date: Date | null } | null>(null);

  const toast = useToast();

  // Chakra colors
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const panelBg = useColorModeValue("gray.50", "gray.700");

  const unionKeys = useMemo(() => {
    const keys = new Set<string>([
      ...Object.keys(leftMap || {}),
      ...Object.keys(rightMap || {}),
    ]);
    return Array.from(keys);
  }, [leftMap, rightMap]);

  const rows = useMemo(() => {
    return unionKeys.map((k) => {
      const a = leftMap?.[k] ?? null;
      const b = rightMap?.[k] ?? null;
      const delta = a != null && b != null ? b - a : null;
      const pct = a != null && b != null && a !== 0 ? ((b - a) / Math.abs(a)) * 100 : null;
      return { k, a, b, delta, pct };
    });
  }, [unionKeys, leftMap, rightMap]);

  const keyDrivers = useMemo(() => {
    const withDelta = rows.filter((r) => r.delta != null);
    return [...withDelta].sort((x, y) => Math.abs(y.delta!) - Math.abs(x.delta!)).slice(0, 5);
  }, [rows]);

  async function handleCompare() {
    try {
      if (!file1 || !file2) {
        toast({ status: "warning", title: "Choose two PDF files first." });
        return;
      }
      if (file1.type !== "application/pdf" || file2.type !== "application/pdf") {
        toast({ status: "error", title: "Both files must be PDFs." });
        return;
      }

      setLoading(true);
      setLeftMap(null);
      setRightMap(null);
      setErrors([]);
      setDateWarnings([]);

      const [lines1Pages, lines2Pages] = await Promise.all([
        extractPdfLinesPerPage(file1),
        extractPdfLinesPerPage(file2),
      ]);
      const lines1 = lines1Pages.flat();
      const lines2 = lines2Pages.flat();

      // Validate income-statement nature
      const errs: string[] = [];
      if (!isIncomeStatement(lines1)) errs.push(`${file1.name} is not recognized as an Income Statement.`);
      if (!isIncomeStatement(lines2)) errs.push(`${file2.name} is not recognized as an Income Statement.`);

      // Parse dates and order older -> newer
      const d1 = parseStatementEndDate(lines1);
      const d2 = parseStatementEndDate(lines2);
      const warnings: string[] = [];
      if (!d1.date) warnings.push(`Could not detect statement date in ${file1.name}.`);
      if (!d2.date) warnings.push(`Could not detect statement date in ${file2.name}.`);

      let leftLines = lines1, rightLines = lines2;
      let leftFile = file1, rightFile = file2;
      let leftDate = d1.date, rightDate = d2.date;

      // If both dates present, ensure left = older, right = newer
      if (d1.date && d2.date) {
        if (d1.date.getTime() > d2.date.getTime()) {
          // swap
          leftLines = lines2; rightLines = lines1;
          leftFile = file2;  rightFile = file1;
          leftDate = d2.date; rightDate = d1.date;
        }
      }

      const leftMoney = extractMoneyMap(leftLines);
      const rightMoney = extractMoneyMap(rightLines);

      const missingLeft = missingRequiredKeys(leftMoney);
      const missingRight = missingRequiredKeys(rightMoney);
      if (missingLeft.length) errs.push(`Older statement missing required items: ${missingLeft.join(", ")}`);
      if (missingRight.length) errs.push(`Newer statement missing required items: ${missingRight.join(", ")}`);

      if (errs.length) {
        setErrors(errs);
        setDateWarnings(warnings);
        setLeftMeta({ name: leftFile.name, date: leftDate ?? null });
        setRightMeta({ name: rightFile.name, date: rightDate ?? null });
        return;
      }

      setLeftMap(leftMoney);
      setRightMap(rightMoney);
      setDateWarnings(warnings);
      setLeftMeta({ name: leftFile.name, date: leftDate ?? null });
      setRightMeta({ name: rightFile.name, date: rightDate ?? null });
    } catch (e: any) {
      console.error(e);
      toast({ status: "error", title: "Failed to process PDFs", description: e?.message ?? "Unknown error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack>
        <Stack position="sticky" top={0} zIndex={1} width="full">
            <Nav pageName={"Statement Analysis"}/>
        </Stack>
        <Box px={{ base: 4, md: 8 }} py={{ base: 6, md: 8 }}>
        <HStack justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
            <Box>
            <Heading size="lg">Income Statement Compare (Older → Newer by Date)</Heading>
            <Text fontSize="sm" color="gray.500">
                We detect each statement’s period end and compare amounts with the older one on the left and the newer on the right.
            </Text>
            </Box>
            <HStack>
            <Button colorScheme="blackAlpha" onClick={handleCompare} isLoading={loading}>
                {loading ? "Processing..." : "Compare"}
            </Button>
            </HStack>
        </HStack>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mb={4}>
            <GridItem>
            <Box border="1px solid" borderColor={borderColor} p={4} bg={cardBg} borderRadius="xl">
                <Text fontWeight="semibold" mb={2}>Statement A</Text>
                <Input type="file" accept="application/pdf" onChange={(e) => setFile1(e.target.files?.[0] ?? null)} size="sm" />
                {file1 && <Text fontSize="xs" color="gray.500" mt={2}>{file1.name} • {(file1.size / 1024 / 1024).toFixed(2)} MB</Text>}
            </Box>
            </GridItem>
            <GridItem>
            <Box border="1px solid" borderColor={borderColor} p={4} bg={cardBg} borderRadius="xl">
                <Text fontWeight="semibold" mb={2}>Statement B</Text>
                <Input type="file" accept="application/pdf" onChange={(e) => setFile2(e.target.files?.[0] ?? null)} size="sm" />
                {file2 && <Text fontSize="xs" color="gray.500" mt={2}>{file2.name} • {(file2.size / 1024 / 1024).toFixed(2)} MB</Text>}
            </Box>
            </GridItem>
        </Grid>

        {loading && (
            <HStack mt={4} color="gray.500">
            <Spinner />
            <Text>Extracting text…</Text>
            </HStack>
        )}

        {!!dateWarnings.length && (
            <Box border="1px solid" borderColor={borderColor} bg={cardBg} borderRadius="xl" p={4} mb={4}>
            <Heading size="sm" mb={2}>Date warnings</Heading>
            <VStack align="stretch" spacing={1}>
                {dateWarnings.map((w, i) => (
                <Tag key={i} colorScheme="orange" variant="subtle">{w}</Tag>
                ))}
            </VStack>
            <Text fontSize="xs" color="gray.500" mt={3}>
                We’ll still compare, but ordering by date may be less accurate if a date wasn’t detected.
            </Text>
            </Box>
        )}

        {!!errors.length && (
            <Box border="1px solid" borderColor={borderColor} bg={cardBg} borderRadius="xl" p={4} mb={4}>
            <Heading size="sm" mb={2}>Validation errors</Heading>
            <VStack align="stretch" spacing={1}>
                {errors.map((e, i) => (
                <Tag key={i} colorScheme="red" variant="subtle">{e}</Tag>
                ))}
            </VStack>
            <Text fontSize="xs" color="gray.500" mt={3}>
                Make sure the statement uses the expected headings and places amounts at the right end of each line.
            </Text>
            </Box>
        )}

        {leftMap && rightMap && !errors.length && (
            <Box border="1px solid" borderColor={borderColor} bg={cardBg} borderRadius="xl" p={4}>
            <HStack justify="space-between" align="center" mb={2}>
                <Heading size="sm">Line-item comparison</Heading>
                <HStack gap={3}>
                <Badge colorScheme="gray">
                    Older (Left): {leftMeta?.name ?? "—"} • Ended {formatDate(leftMeta?.date ?? null)}
                </Badge>
                <Badge colorScheme="purple">
                    Newer (Right): {rightMeta?.name ?? "—"} • Ended {formatDate(rightMeta?.date ?? null)}
                </Badge>
                </HStack>
            </HStack>
            <Divider mb={3} />

            <Table size="sm" variant="simple">
                <Thead>
                <Tr>
                    <Th>Line item</Th>
                    <Th isNumeric>Older (Ended {formatDate(leftMeta?.date ?? null)})</Th>
                    <Th isNumeric>Newer (Ended {formatDate(rightMeta?.date ?? null)})</Th>
                    <Th isNumeric>Δ (New − Old)</Th>
                    <Th isNumeric>% Δ</Th>
                </Tr>
                </Thead>
                <Tbody>
                {rows.map(({ k, a, b, delta, pct }) => {
                    const color =
                    delta == null ? undefined :
                    delta > 0 ? "green.500" :
                    delta < 0 ? "red.500" : undefined;
                    return (
                    <Tr key={k}>
                        <Td><Text>{k}</Text></Td>
                        <Td isNumeric>{a != null ? a.toLocaleString() : "—"}</Td>
                        <Td isNumeric>{b != null ? b.toLocaleString() : "—"}</Td>
                        <Td isNumeric>
                        {delta != null ? (
                            <Tooltip label={delta}>
                            <Text color={color}>{delta.toLocaleString()}</Text>
                            </Tooltip>
                        ) : "—"}
                        </Td>
                        <Td isNumeric>{pct != null ? `${pct.toFixed(1)}%` : "—"}</Td>
                    </Tr>
                    );
                })}
                </Tbody>
            </Table>

            <Box mt={6} bg={panelBg} p={3} borderRadius="md">
                <Heading size="xs" mb={2}>Key drivers</Heading>
                {keyDrivers.length ? (
                <VStack align="stretch" spacing={1}>
                    {keyDrivers.map(({ k, delta, pct }) => (
                    <HStack key={k} justify="space-between">
                        <Text>{k}</Text>
                        <HStack>
                        <Tag colorScheme={delta! > 0 ? "green" : delta! < 0 ? "red" : "gray"} variant="subtle">
                            {delta! > 0 ? "↑" : delta! < 0 ? "↓" : "—"} {Math.abs(delta!).toLocaleString()}
                        </Tag>
                        <Text fontSize="xs" color="gray.500">{pct != null ? `${pct.toFixed(1)}%` : "—"}</Text>
                        </HStack>
                    </HStack>
                    ))}
                </VStack>
                ) : (
                <Text fontSize="sm" color="gray.500">No significant changes detected.</Text>
                )}
            </Box>
            </Box>
        )}
        </Box>
    </Stack>
  );
}